import { ReactNode, useId, type RefObject } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useModalDialog } from '../../hooks/useModalDialog'

interface DialogProps {
  isOpen: boolean
  onClose: () => void
  title: string
  children: ReactNode
  size?: 'sm' | 'md' | 'lg'
  preventClose?: boolean
  initialFocus?: RefObject<HTMLElement>
}

export default function Dialog({ isOpen, onClose, title, children, size = 'md', preventClose = false, initialFocus }: DialogProps) {
  const titleId = useId()
  const modal = useModalDialog({ open: isOpen, onClose, preventClose, initialFocus })
  if (!isOpen) return null

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
  }

  return (
    <dialog {...modal} aria-labelledby={titleId} aria-modal="true" tabIndex={-1} className="fixed inset-0 z-50 m-0 h-dvh max-h-none w-screen max-w-none border-0 bg-transparent p-0 text-slate-900 dark:text-slate-100 backdrop:bg-black/50">
    <div className="flex h-full items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={() => { if (!preventClose) onClose() }}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div className={`relative flex max-h-full w-full flex-col ${sizeClasses[size]} overflow-hidden bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-2xl`}>
        <div className="h-1 shrink-0 bg-gradient-to-r from-orange-500 via-amber-400 to-orange-600" />
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 id={titleId} className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={preventClose}
            aria-label="Fermer la fenêtre"
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="min-h-0 overflow-y-auto px-4 py-4 sm:px-6">
          {children}
        </div>
      </div>
    </div>
    </dialog>
  )
}
